import mixpanel from 'mixpanel-browser';
import { getDomainAndPort } from '../../api/Utils';
import { ICompany, ICompanyServer } from '../../store/companies/companiesTypes';

interface IVisitedTaskGroupByTemplates {
  [key: number | string]: number;
}

const actions = {
  identify: (id: string): void => {
    mixpanel.identify(id);
  },
  alias: (id: string): void => {
    mixpanel.alias(id);
  },
  track: (name: string, props: any): void => {
    mixpanel.track(name, props);
  },
  people: {
    set: (props: any): void => {
      mixpanel.people.set(props);
    },
  },
};

export default class UserMixpanel {
  private _activeUserId: number = 0;
  private _name: string = '';
  private _email: string = '';
  private _phoneNumber: string = '';
  private _companyName: string = '';
  private _domain: string = '';
  private _isLogginCalled: boolean = false;
  private _roles: Array<string> | null = null;
  private _visitedTasksCount: number = 0;
  private _visitedTaskGroupByTemplates: IVisitedTaskGroupByTemplates | null =
    null;
  private _numberOfSelfFilledSurveysPDP: Record<string, number> = {};
  private _numberOfOtherFilledSurveysPDP: Record<string, number> = {};
  private _numberOfEnteredPDP: number = 0;
  private _numberOfViewedCourses: number = 0;
  private _numberOfSeenAcademicReportsInBatch: number = 0;
  private _numberOfSeenSingleAcademicReport: number = 0;
  private _numberOfSeenSelfAcademicReports: number = 0;
  private _selfAssessmentCounter: number = 0;
  private _otherAssessmentCounter: number = 0;
  private _numberOfExportExcelAcademicReports: number = 0;
  private _numberOfPrintAcademicReports: number = 0;

  private static instance: UserMixpanel | null = null;

  private constructor(activeUserId: number = 0) {
    if (UserMixpanel.instance) {
      return UserMixpanel.instance;
    }
    this._activeUserId = activeUserId;
    this._domain = getDomainAndPort().domain;
    this._isLogginCalled = false;

    mixpanel.init('9cccf49c0134a6f279194265773ab61f', {
      api_host: getDomainAndPort().protocolDomain,
    });
    UserMixpanel.instance = this;

    setTimeout(() => {
      actions.identify(`${this._companyName}-${activeUserId.toString()}`);
      this._isLogginCalled = true;
      actions.track('Successful login', {
        roles: this._roles,
        companyName: this._companyName,
      });
      actions.people.set({
        $email: this._email,
        $phone_number: this._phoneNumber,
        $name: this._name,
        $domain: this._domain,
      });
    }, 2 * 60000);
  }

  public static getInstance(activeUserId: number = 0): UserMixpanel {
    if (UserMixpanel.instance) {
      return UserMixpanel.instance;
    }
    UserMixpanel.instance = new UserMixpanel(activeUserId);
    return UserMixpanel.instance;
  }

  public getCompanyName() {
    return this._companyName;
  }

  public getRoles() {
    return this._roles;
  }

  public visitedTasksCountHandler(
    count = 1,
    type: 'increase' | 'reset' = 'increase'
  ) {
    switch (type) {
      case 'increase':
        this._visitedTasksCount += count;
        break;
      case 'reset':
        this._visitedTasksCount = 0;
        break;
      default:
        this._visitedTasksCount += count;
    }
  }

  public handleSetVisitedTaskCountByTemplates(templateName: string) {
    const current = this._visitedTaskGroupByTemplates?.[templateName] ?? 0;

    this._visitedTaskGroupByTemplates = {
      ...this._visitedTaskGroupByTemplates,
      [templateName]: current + 1,
    };
  }

  public getTotalTaskClickedCount(): number {
    return this._visitedTasksCount;
  }

  public getTotalTaskGroupbyTemplateClickedCount(): any {
    return this._visitedTaskGroupByTemplates;
  }

  private setIdentity() {
    if (mixpanel.get_distinct_id() === 'anonymous') {
      actions.identify(`${this._companyName}-${this._activeUserId.toString()}`);
    }
  }

  getActiveUserId(): number {
    return this._activeUserId;
  }

  setRoles(roles: string[]): void {
    this._roles = roles;
  }

  setUsersInfo(name: string, email: string, phoneNumber: string): void {
    this._name = name;
    this._email = email;
    this._phoneNumber = phoneNumber;
  }

  setRoleNames(company: ICompany | ICompanyServer): void {
    const roles = company.members?.[this._activeUserId]?.roles;
    const roleNames: string[] = [];
    for (const roleId in roles) {
      roleNames.push(roles[roleId]?.name);
    }
    this._roles = roleNames;
  }

  setCompanyName(companyName: string): void {
    this._companyName = companyName;
  }

  sendChecklistOrIndicatorSavedTrack(
    checklistOrIndicatorName: string = ''
  ): void {
    this.setIdentity();
    actions.track('Checklist or Indicator added Track', {
      name: checklistOrIndicatorName,
      roles: this._roles,
      companyName: this._companyName,
    });
  }

  sendVisitDocumentTrack(name: string = ''): void {
    this.setIdentity();
    actions.track('Visit Document Track', {
      name: name,
      roles: this._roles,
      companyName: this._companyName,
    });
  }

  sendUploadDocumentTrack(name: string = ''): void {
    this.setIdentity();
    actions.track('Upload Document Track', {
      name: name,
      roles: this._roles,
      companyName: this._companyName,
    });
  }

  sendCreateEditPageTrack(name: string = ''): void {
    this.setIdentity();
    actions.track('Create/Edit Page Track', {
      name: name,
      roles: this._roles,
      companyName: this._companyName,
    });
  }

  sendCreateTaskTrack(name: string = '', templateName: string = ''): void {
    this.setIdentity();
    actions.track('Create Task Track', {
      taskName: name,
      templateName: templateName,
      roles: this._roles,
      companyName: this._companyName,
    });
  }

  sendCreateReportFromTaskTrack(name: string = ''): void {
    this.setIdentity();
    actions.track('Create Report From Task', {
      name,
      roles: this._roles,
      companyName: this._companyName,
    });
  }

  sendVistedTaskInBoardView(name: string): void {
    this.setIdentity();
    actions.track('Visited Task In Board View', {
      name: name,
      roles: this._roles,
      companyName: this._companyName,
    });
  }

  sendUserFeedbackTrack(
    phase: string,
    rating: number,
    description: string = ''
  ): void {
    this.setIdentity();
    actions.track('Exam Rating Track', {
      phase: phase,
      rating: rating,
      description: description,
      companyName: this._companyName,
      roles: this._roles,
    });
  }

  sendLogoutTrack(): void {
    if (this._isLogginCalled) {
      actions.track('Successful Logout', {
        roles: this._roles,
        companyName: this._companyName,
        visitedTasks: this._visitedTasksCount,
        ...this._numberOfSelfFilledSurveysPDP,
        ...this._numberOfOtherFilledSurveysPDP,
        enteredPDP: this._numberOfEnteredPDP,
        viewedCourses: this._numberOfViewedCourses,
        seenSelfAcademicReports: this._numberOfSeenSelfAcademicReports,
        seenSingleOtherAcademicReports: this._numberOfSeenSingleAcademicReport,
        seenAcademicReportsInBatch: this._numberOfSeenAcademicReportsInBatch,
        selfAssessmentCounter: this._selfAssessmentCounter,
        otherAssessmentCounter: this._otherAssessmentCounter,
        numberOfExportExcelAcademicReports:
          this._numberOfExportExcelAcademicReports,
        numberOfPrintAcademicReports: this._numberOfPrintAcademicReports,
      });
      this.visitedTasksCountHandler(0, 'reset');
      this.resetPDPEvents();
    } else {
      setTimeout((): void => {
        actions.track('Successful Logout', {
          roles: this._roles,
          companyName: this._companyName,
          visitedTasks: this._visitedTasksCount,
          ...this._numberOfSelfFilledSurveysPDP,
          ...this._numberOfOtherFilledSurveysPDP,
          enteredPDP: this._numberOfEnteredPDP,
          viewedCourses: this._numberOfViewedCourses,
          seenSelfAcademicReports: this._numberOfSeenSelfAcademicReports,
          seenSingleOtherAcademicReports:
            this._numberOfSeenSingleAcademicReport,
          seenAcademicReportsInBatch: this._numberOfSeenAcademicReportsInBatch,
          selfAssessmentCounter: this._selfAssessmentCounter,
          otherAssessmentCounter: this._otherAssessmentCounter,
          numberOfExportExcelAcademicReports:
            this._numberOfExportExcelAcademicReports,
          numberOfPrintAcademicReports: this._numberOfPrintAcademicReports,
        });
        this.visitedTasksCountHandler(0, 'reset');
        this.resetPDPEvents();
      }, 2 * 60000);
    }
  }

  public handleAddNumberOfFilledSurveysPDP(
    phaseId: string,
    userId: number
  ): void {
    const isSelf = userId === this._activeUserId;
    if (isSelf) {
      this._numberOfSelfFilledSurveysPDP[phaseId] =
        (this._numberOfSelfFilledSurveysPDP[phaseId] || 0) + 1;
    } else {
      this._numberOfOtherFilledSurveysPDP[phaseId] =
        (this._numberOfOtherFilledSurveysPDP[phaseId] || 0) + 1;
    }
  }

  public resetNumberOfFilledSurveysPDP(): void {
    this._numberOfSelfFilledSurveysPDP = {};
    this._numberOfOtherFilledSurveysPDP = {};
  }

  public handleAddNumberOfEnteredPDP(): void {
    this._numberOfEnteredPDP++;
  }

  public handleResetNumberOfEnteredPDP(): void {
    this._numberOfEnteredPDP = 0;
  }

  public handleAddNumberOfSeenAcademicReports(
    inBatch: boolean = false,
    seenBySelf: boolean = true
  ): void {
    if (inBatch) {
      this._numberOfSeenAcademicReportsInBatch++;
    } else if (seenBySelf) {
      this._numberOfSeenSelfAcademicReports++;
    } else {
      this._numberOfSeenSingleAcademicReport++;
    }
  }

  public handleResetNumberOfSeenAcademicReports(): void {
    this._numberOfSeenAcademicReportsInBatch = 0;
    this._numberOfSeenSelfAcademicReports = 0;
    this._numberOfSeenSingleAcademicReport = 0;
  }

  public getNumberOfEnteredPDP(): number {
    return this._numberOfEnteredPDP;
  }

  public handleAddAssessmentCounter(isSelf: boolean): void {
    if (isSelf) {
      this._selfAssessmentCounter++;
    } else {
      this._otherAssessmentCounter++;
    }
  }

  public handleResetAssessmentCounter(): void {
    this._selfAssessmentCounter = 0;
    this._otherAssessmentCounter = 0;
  }

  public handleAddNumberOfViewedCourses(): void {
    this._numberOfViewedCourses++;
  }

  public resetNumberOfViewedCourses(): void {
    this._numberOfViewedCourses = 0;
  }

  public resetPDPEvents(): void {
    this.resetNumberOfFilledSurveysPDP();
    this.resetNumberOfViewedCourses();
    this.handleResetNumberOfEnteredPDP();
    this.handleResetNumberOfSeenAcademicReports();
    this.handleResetAssessmentCounter();
    this.resetExportAcademicReportCounter();
    this.resetPrintAcademicReportCounter();
  }

  public handleAddExportAcademicReportCounter(): void {
    this._numberOfExportExcelAcademicReports++;
  }

  public handleAddPrintAcademicReportCounter(): void {
    this._numberOfPrintAcademicReports++;
  }

  public resetExportAcademicReportCounter(): void {
    this._numberOfExportExcelAcademicReports = 0;
  }

  public resetPrintAcademicReportCounter(): void {
    this._numberOfPrintAcademicReports = 0;
  }
}

export const Mixpanel = actions;
